// StaffContext.tsx - Comprehensive Staff Management Context
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { toast } from 'sonner';

// TYPE DEFINITIONS


export type StaffRole = 'admin' | 'lab_scientist' | 'staff' | 'supervisor';


export type StaffStatus = 'active' | 'inactive' | 'on_leave' | 'suspended';

/**
 * Laboratory departments where staff can be assigned
 */
export type StaffDepartment = 'hematology' | 'biochemistry' | 'microbiology' | 'pathology' | 'radiology' | 'administration' | 'general';

/**
 * Granular permission flags for role-based access control
 * Each boolean represents ability to perform specific actions
 */
export interface StaffPermissions {
  canAcceptRequests: boolean;
  canProcessTests: boolean;
  canCompleteTests: boolean;
  canSendResults: boolean;
  canManageStaff: boolean;
  canViewReports: boolean;
  canEditSettings: boolean;
  canDeleteRecords: boolean;
  canManageInventory: boolean;
  canApproveResults: boolean;
}

export interface StaffMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: StaffRole;
  department: StaffDepartment;
  status: StaffStatus;
  permissions: StaffPermissions;
  avatar?: string;
  dateOfBirth?: string;
  address?: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  employeeId?: string;
  hireDate: string;
  licenseNumber?: string;
  certifications?: string[];
  specializations?: string[];
  workSchedule?: {
    monday?: { start: string; end: string };
    tuesday?: { start: string; end: string };
    wednesday?: { start: string; end: string };
    thursday?: { start: string; end: string };
    friday?: { start: string; end: string };
    saturday?: { start: string; end: string };
    sunday?: { start: string; end: string };
  };
  testsCompleted?: number;
  averageCompletionTime?: string;
  accuracyRate?: number;
  lastActive?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  notes?: string;
}

/**
 * Activity log entry for staff actions
 * Tracks operations performed by and on staff members
 */
export interface StaffActivity {
  id: string;
  staffId: string;
  staffName: string;
  action: 'login' | 'logout' | 'test_accepted' | 'test_completed' | 'result_sent' | 'setting_changed' | 'staff_added' | 'staff_edited';
  description: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

/**
 * Performance metrics for a staff member over a period
 * Used for analytics and performance evaluation
 */
export interface StaffPerformance {
  staffId: string;
  period: 'daily' | 'weekly' | 'monthly' | 'yearly';
  testsCompleted: number;
  averageCompletionTime: string;
  accuracyRate: number;
  patientsFeedback?: number;
  punctualityScore?: number;
}

/**
 * Complete context API for staff management
 * Provides all operations for CRUD, filtering, analytics, and administration
 */
interface StaffContextType {
  // State
  staffMembers: StaffMember[];
  activities: StaffActivity[];
  loading: boolean;

  // CRUD Operations
  addStaff: (staff: Omit<StaffMember, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateStaff: (id: string, updates: Partial<StaffMember>) => Promise<void>;
  deleteStaff: (id: string) => Promise<void>;
  getStaffById: (id: string) => StaffMember | undefined;

  // Status Management
  activateStaff: (id: string) => Promise<void>;
  deactivateStaff: (id: string) => Promise<void>;
  suspendStaff: (id: string, reason?: string) => Promise<void>;
  setOnLeave: (id: string, startDate: string, endDate: string) => Promise<void>;

  // Permissions Management
  updatePermissions: (id: string, permissions: Partial<StaffPermissions>) => Promise<void>;
  getStaffPermissions: (id: string) => StaffPermissions | undefined;

  // Filtering & Search
  getActiveStaff: () => StaffMember[];
  getInactiveStaff: () => StaffMember[];
  getStaffByRole: (role: StaffRole) => StaffMember[];
  getStaffByDepartment: (department: StaffDepartment) => StaffMember[];
  searchStaff: (query: string) => StaffMember[];

  // Activity Tracking
  logActivity: (activity: Omit<StaffActivity, 'id' | 'timestamp'>) => void;
  getStaffActivities: (staffId: string, limit?: number) => StaffActivity[];
  getRecentActivities: (limit?: number) => StaffActivity[];

  // Performance & Analytics
  getStaffPerformance: (staffId: string, period: StaffPerformance['period']) => StaffPerformance | null;
  getTopPerformers: (limit?: number) => StaffMember[];
  getDepartmentStats: (department: StaffDepartment) => {
    totalStaff: number;
    activeStaff: number;
    testsCompleted: number;
  };

  // Bulk Operations
  bulkUpdateStatus: (ids: string[], status: StaffStatus) => Promise<void>;
  bulkDelete: (ids: string[]) => Promise<void>;

  // Utility
  refreshStaff: () => Promise<void>;
  exportStaffData: () => void;
  getTotalStaffCount: () => number;
  getStaffUtilization: () => number;

  // LocalStorage Management
  clearAllData: () => void;
  resetToDefaults: () => void;
}

// TODO: BACKEND INTEGRATION - REMOVE BELOW WHEN CONNECTED TO API

/**
 * TODO: BACKEND - Replace with API call to GET /api/staff/roles/permissions
 * Default permissions configuration for each role
 */
const getDefaultPermissions = (role: StaffRole): StaffPermissions => {
  const basePermissions: StaffPermissions = {
    canAcceptRequests: false,
    canProcessTests: false,
    canCompleteTests: false,
    canSendResults: false,
    canManageStaff: false,
    canViewReports: false,
    canEditSettings: false,
    canDeleteRecords: false,
    canManageInventory: false,
    canApproveResults: false,
  };

  switch (role) {
    case 'admin':
      return {
        canAcceptRequests: true,
        canProcessTests: true,
        canCompleteTests: true,
        canSendResults: true,
        canManageStaff: true,
        canViewReports: true,
        canEditSettings: true,
        canDeleteRecords: true,
        canManageInventory: true,
        canApproveResults: true,
      };
    case 'supervisor':
      return {
        ...basePermissions,
        canAcceptRequests: true,
        canProcessTests: true,
        canCompleteTests: true,
        canSendResults: true,
        canViewReports: true,
        canManageInventory: true,
        canApproveResults: true,
      };
    case 'lab_scientist':
      return {
        ...basePermissions,
        canAcceptRequests: true,
        canProcessTests: true,
        canCompleteTests: true,
        canSendResults: true,
        canViewReports: true,
      };
    case 'staff':
      return {
        ...basePermissions,
        canAcceptRequests: true,
        canProcessTests: true,
        canViewReports: true,
      };
    default:
      return basePermissions;
  }
};

/**
 * TODO: BACKEND - Replace with API call to GET /api/staff
 * Sample staff data - remove when backend is integrated
 */
const DEFAULT_STAFF_MEMBERS: StaffMember[] = [];

/**
 * TODO: BACKEND - Replace with API call to GET /api/staff/activities
 * Sample activity data - remove when backend is integrated
 */
const DEFAULT_ACTIVITIES: StaffActivity[] = [];

/**
 * TODO: BACKEND - Remove localStorage keys - use API endpoints instead
 * Local storage keys for persistence
 */
const STORAGE_KEYS = {
  STAFF_MEMBERS: 'labStaffMembers',
  STAFF_ACTIVITIES: 'labStaffActivities',
};

/**
 * TODO: SECURITY & LOGGING - Replace console.error with centralized error tracking
 * Implement proper error logging service (Sentry, Datadog, etc.) instead of console
 * TODO: BACKEND - Remove this helper - use API calls instead
 * Loads data from localStorage with fallback to default values
 */
const loadFromLocalStorage = <T,>(key: string, defaultValue: T): T => { return defaultValue; };

/**
 * TODO: SECURITY & LOGGING - Replace console.error with centralized error tracking
 * Implement proper error logging service (Sentry, Datadog, etc.) instead of console
 * TODO: BACKEND - Remove this helper - use API calls instead
 * Saves data to localStorage
 */
const saveToLocalStorage = <T,>(key: string, value: T): void => { /* Staff records require backend persistence. */ };

// END OF SECTION TO REMOVE

// CONTEXT CREATION

const StaffContext = createContext<StaffContextType | undefined>(undefined);

// PROVIDER COMPONENT

/**
 * StaffProvider - Manages all staff-related state and operations
 * Provides comprehensive staff management API throughout the application
 * TODO: SECURITY - All console logging removed to prevent exposure of internal state
 */
export const StaffProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(true);

  // TODO: BACKEND - Replace with API call to GET /api/staff
  // Initialize state from localStorage - REMOVE THIS
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>(() =>
    loadFromLocalStorage(STORAGE_KEYS.STAFF_MEMBERS, DEFAULT_STAFF_MEMBERS)
  );

  // TODO: BACKEND - Replace with API call to GET /api/staff/activities
  // Initialize state from localStorage - REMOVE THIS
  const [activities, setActivities] = useState<StaffActivity[]>(() =>
    loadFromLocalStorage(STORAGE_KEYS.STAFF_ACTIVITIES, DEFAULT_ACTIVITIES)
  );

  // INITIALIZATION

  // TODO: BACKEND - Replace with API call to GET /api/staff
  useEffect(() => {
    setLoading(false);
  }, []);

  // AUTO-SAVE EFFECTS - TODO: REMOVE THESE WHEN CONNECTED TO BACKEND

  // TODO: BACKEND - Replace with API call to PUT /api/staff
  // Auto-save staff members to localStorage - REMOVE THIS
  useEffect(() => {
    if (!loading) {
      saveToLocalStorage(STORAGE_KEYS.STAFF_MEMBERS, staffMembers);
    }
  }, [staffMembers, loading]);

  // TODO: BACKEND - Replace with API call to PUT /api/staff/activities
  // Auto-save activities to localStorage - REMOVE THIS
  useEffect(() => {
    if (!loading) {
      saveToLocalStorage(STORAGE_KEYS.STAFF_ACTIVITIES, activities);
    }
  }, [activities, loading]);

  // CRUD OPERATIONS

  /**
   * Adds a new staff member to the system
   * Generates unique ID and logs the activity
   *
   * TODO: BACKEND - POST /api/staff
   * TODO: SECURITY - No console logging for production (prevents state exposure)
   */
  const addStaff = useCallback(async (staffData: Omit<StaffMember, 'id' | 'createdAt' | 'updatedAt'>) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        }, [staffMembers.length]);

  /**
   * Updates an existing staff member's information
   *
   * TODO: BACKEND - PUT /api/staff/{id}
   * TODO: SECURITY - No console logging for production (prevents state exposure)
   */
  const updateStaff = useCallback(async (id: string, updates: Partial<StaffMember>) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        }, [staffMembers]);

  /**
   * Deletes a staff member from the system
   *
   * TODO: BACKEND - DELETE /api/staff/{id}
   * TODO: SECURITY - No console logging for production (prevents state exposure)
   */
  const deleteStaff = useCallback(async (id: string) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        }, [staffMembers]);

  /**
   * Retrieves a specific staff member by ID
   * Returns undefined if not found
   */
  const getStaffById = useCallback((id: string) => {
    return staffMembers.find((staff) => staff.id === id);
  }, [staffMembers]);

  // STATUS MANAGEMENT

  /**
   * Activates a staff member (sets status to 'active')
   *
   * TODO: BACKEND - PUT /api/staff/{id}/status
   */
  const activateStaff = useCallback(async (id: string) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        }, [updateStaff]);

  /**
   * Deactivates a staff member (sets status to 'inactive')
   *
   * TODO: BACKEND - PUT /api/staff/{id}/status
   */
  const deactivateStaff = useCallback(async (id: string) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        }, [updateStaff]);

  /**
   * Suspends a staff member with optional reason
   *
   * TODO: BACKEND - PUT /api/staff/{id}/status
   */
  const suspendStaff = useCallback(async (id: string, reason?: string) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        }, [updateStaff]);

  /**
   * Sets a staff member on leave for a specified period
   *
   * TODO: BACKEND - PUT /api/staff/{id}/status
   */
  const setOnLeave = useCallback(async (id: string, startDate: string, endDate: string) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        }, [updateStaff]);

  // PERMISSIONS MANAGEMENT

  /**
   * Updates permissions for a staff member
   * Merges new permissions with existing ones
   *
   * TODO: BACKEND - PUT /api/staff/{id}/permissions
   */
  const updatePermissions = useCallback(async (id: string, permissions: Partial<StaffPermissions>) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        }, [staffMembers, updateStaff]);

  /**
   * Retrieves permissions for a specific staff member
   */
  const getStaffPermissions = useCallback((id: string) => {
    const staff = staffMembers.find(s => s.id === id);
    return staff?.permissions;
  }, [staffMembers]);

  // FILTERING & SEARCH
  /**
   * Gets all currently active staff members
   */
  const getActiveStaff = useCallback(() => {
    return staffMembers.filter((staff) => staff.status === 'active');
  }, [staffMembers]);

  /**
   * Gets all inactive staff members (not active, on leave, or suspended)
   */
  const getInactiveStaff = useCallback(() => {
    return staffMembers.filter((staff) => staff.status !== 'active');
  }, [staffMembers]);

  /**
   * Gets staff members by specific role
   */
  const getStaffByRole = useCallback((role: StaffRole) => {
    return staffMembers.filter((staff) => staff.role === role);
  }, [staffMembers]);

  /**
   * Gets staff members assigned to a specific department
   */
  const getStaffByDepartment = useCallback((department: StaffDepartment) => {
    return staffMembers.filter((staff) => staff.department === department);
  }, [staffMembers]);

  /**
   * Searches staff by name, email, phone, ID, or role
   * Case-insensitive search
   */
  const searchStaff = useCallback((query: string) => {
    const lowerQuery = query.toLowerCase();
    return staffMembers.filter(
      (staff) =>
        staff.name.toLowerCase().includes(lowerQuery) ||
        staff.email.toLowerCase().includes(lowerQuery) ||
        staff.phone.includes(lowerQuery) ||
        staff.id.toLowerCase().includes(lowerQuery) ||
        staff.role.toLowerCase().includes(lowerQuery)
    );
  }, [staffMembers]);

  // ACTIVITY TRACKING
  /**
   * Logs an activity for a staff member
   * Automatically generates ID and timestamp
   * Keeps last 1000 activities to prevent memory bloat
   *
   * TODO: BACKEND - POST /api/staff/activities
   */
  const logActivity = useCallback((activity: Omit<StaffActivity, 'id' | 'timestamp'>) => {
    const newActivity: StaffActivity = {
      ...activity,
      id: `ACT-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
    };

    setActivities((prev) => [newActivity, ...prev].slice(0, 1000));
  }, []);

  /**
   * Gets activities for a specific staff member
   * Limited to most recent entries (default 50)
   *
   * TODO: BACKEND - GET /api/staff/{id}/activities
   */
  const getStaffActivities = useCallback((staffId: string, limit: number = 50) => {
    return activities
      .filter((activity) => activity.staffId === staffId)
      .slice(0, limit);
  }, [activities]);

  /**
   * Gets recent activities across all staff
   * Limited to most recent entries (default 20)
   *
   * TODO: BACKEND - GET /api/staff/activities/recent
   */
  const getRecentActivities = useCallback((limit: number = 20) => {
    return activities.slice(0, limit);
  }, [activities]);

  // PERFORMANCE & ANALYTICS

  /**
   * Gets performance metrics for a specific staff member
   * Returns performance data for specified period
   *
   * TODO: BACKEND - GET /api/staff/{id}/performance
   */
  const getStaffPerformance = useCallback((staffId: string, period: StaffPerformance['period']): StaffPerformance | null => {
    const staff = staffMembers.find(s => s.id === staffId);
    if (!staff) return null;

    return {
      staffId,
      period,
      testsCompleted: staff.testsCompleted || 0,
      averageCompletionTime: staff.averageCompletionTime || '0h',
      accuracyRate: staff.accuracyRate || 0,
      patientsFeedback: 0,
      punctualityScore: 0,
    };
  }, [staffMembers]);

  /**
   * Gets top performing active staff members
   * Sorted by tests completed (descending)
   */
  const getTopPerformers = useCallback((limit: number = 5) => {
    return [...staffMembers]
      .filter(s => s.status === 'active')
      .sort((a, b) => (b.testsCompleted || 0) - (a.testsCompleted || 0))
      .slice(0, limit);
  }, [staffMembers]);

  /**
   * Gets aggregated statistics for a specific department
   * Returns total, active, and test completion counts
   */
  const getDepartmentStats = useCallback((department: StaffDepartment) => {
    const deptStaff = staffMembers.filter(s => s.department === department);
    return {
      totalStaff: deptStaff.length,
      activeStaff: deptStaff.filter(s => s.status === 'active').length,
      testsCompleted: deptStaff.reduce((sum, s) => sum + (s.testsCompleted || 0), 0),
    };
  }, [staffMembers]);

  // BULK OPERATIONS
  /**
   * Updates status for multiple staff members at once
   *
   * TODO: BACKEND - PUT /api/staff/bulk/status
   * TODO: SECURITY - No console logging for production (prevents state exposure)
   */
  const bulkUpdateStatus = useCallback(async (ids: string[], status: StaffStatus) => {
    try {
      setStaffMembers((prev) =>
        prev.map((staff) =>
          ids.includes(staff.id)
            ? { ...staff, status, updatedAt: new Date().toISOString() }
            : staff
        )
      );
      toast.success(`${ids.length} staff members updated`);
    } catch (error) {
      // TODO: SECURITY & LOGGING - Use centralized error tracking (Sentry, etc.) instead of console.error
      toast.error('Failed to update staff members');
      throw error;
    }
  }, []);

  /**
   * Deletes multiple staff members at once
   *
   * TODO: BACKEND - DELETE /api/staff/bulk
   * TODO: SECURITY - No console logging for production (prevents state exposure)
   */
  const bulkDelete = useCallback(async (ids: string[]) => {
    try {
      setStaffMembers((prev) => prev.filter((staff) => !ids.includes(staff.id)));
      toast.success(`${ids.length} staff members deleted`);
    } catch (error) {
      // TODO: SECURITY & LOGGING - Use centralized error tracking (Sentry, etc.) instead of console.error
      toast.error('Failed to delete staff members');
      throw error;
    }
  }, []);

  // UTILITY FUNCTIONS
  /**
   * Refreshes staff data from storage
   * Useful for syncing with external changes
   *
   * TODO: BACKEND - GET /api/staff
   * TODO: SECURITY - No console logging for production (prevents state exposure)
   */
  const refreshStaff = useCallback(async () => {
    setLoading(true);
    try {
      // TODO: BACKEND - Replace with API call
      const storedStaff = loadFromLocalStorage(STORAGE_KEYS.STAFF_MEMBERS, DEFAULT_STAFF_MEMBERS);
      const storedActivities = loadFromLocalStorage(STORAGE_KEYS.STAFF_ACTIVITIES, DEFAULT_ACTIVITIES);

      setStaffMembers(storedStaff);
      setActivities(storedActivities);

      toast.success('Staff data refreshed');
    } catch (error) {
      // TODO: SECURITY & LOGGING - Use centralized error tracking (Sentry, etc.) instead of console.error
      toast.error('Failed to refresh staff data');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Exports staff data as JSON file
   * Downloads to user's device
   * TODO: SECURITY - No console logging for production (prevents state exposure)
   */
  const exportStaffData = useCallback(() => {
    try {
      const dataStr = JSON.stringify(staffMembers, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `staff-data-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Staff data exported successfully');
    } catch (error) {
      // TODO: SECURITY & LOGGING - Use centralized error tracking (Sentry, etc.) instead of console.error
      toast.error('Failed to export staff data');
    }
  }, [staffMembers]);

  /**
   * Gets total count of all staff members
   */
  const getTotalStaffCount = useCallback(() => {
    return staffMembers.length;
  }, [staffMembers]);

  /**
   * Gets staff utilization percentage (active / total)
   * Returns 0 if no staff members
   */
  const getStaffUtilization = useCallback(() => {
    const activeStaff = staffMembers.filter(s => s.status === 'active').length;
    const totalStaff = staffMembers.length;
    return totalStaff > 0 ? (activeStaff / totalStaff) * 100 : 0;
  }, [staffMembers]);

  // LOCALSTORAGE MANAGEMENT

  /**
   * Clears all staff data from storage
   * WARNING: This is irreversible
   *
   * TODO: BACKEND - Consider if this operation is needed with API backend
   */
  const clearAllData = useCallback(() => {
    // TODO: BACKEND - Remove localStorage operations
    localStorage.removeItem(STORAGE_KEYS.STAFF_MEMBERS);
    localStorage.removeItem(STORAGE_KEYS.STAFF_ACTIVITIES);

    setStaffMembers([]);
    setActivities([]);

    toast.success('All staff data cleared');
  }, []);

  /**
   * Resets all staff data to default values
   * WARNING: This is irreversible
   *
   * TODO: BACKEND - Remove this function when using API backend
   */
  const resetToDefaults = useCallback(() => {
    setStaffMembers(DEFAULT_STAFF_MEMBERS);
    setActivities(DEFAULT_ACTIVITIES);

    toast.success('Staff data reset to defaults');
  }, []);

  // PROVIDER VALUE EXPORT

  const value: StaffContextType = {
    // State
    staffMembers,
    activities,
    loading,

    // CRUD Operations
    addStaff,
    updateStaff,
    deleteStaff,
    getStaffById,

    // Status Management
    activateStaff,
    deactivateStaff,
    suspendStaff,
    setOnLeave,

    // Permissions Management
    updatePermissions,
    getStaffPermissions,

    // Filtering & Search
    getActiveStaff,
    getInactiveStaff,
    getStaffByRole,
    getStaffByDepartment,
    searchStaff,

    // Activity Tracking
    logActivity,
    getStaffActivities,
    getRecentActivities,

    // Performance & Analytics
    getStaffPerformance,
    getTopPerformers,
    getDepartmentStats,

    // Bulk Operations
    bulkUpdateStatus,
    bulkDelete,

    // Utility
    refreshStaff,
    exportStaffData,
    getTotalStaffCount,
    getStaffUtilization,

    // LocalStorage Management
    clearAllData,
    resetToDefaults,
  };

  return <StaffContext.Provider value={value}>{children}</StaffContext.Provider>;
};

// CUSTOM HOOK

/**
 * useStaff hook - Access staff management context anywhere in the application
 * Must be used within a StaffProvider
 *
 * @throws Error if used outside of StaffProvider
 * @returns {StaffContextType} Complete staff management API
 *
 * @example
 * const { staffMembers, addStaff, updateStaff } = useStaff();
 */
export const useStaff = () => {
  const context = useContext(StaffContext);

  if (context === undefined) {
    throw new Error('useStaff must be used within a StaffProvider');
  }

  return context;
};

// HELPER FUNCTIONS & UTILITIES

/**
 * Converts staff role type to user-friendly display name
 *
 * @param role - Staff role type
 * @returns Human-readable role name
 */
export const getRoleDisplayName = (role: StaffRole): string => {
  const roleMap = {
    admin: 'Administrator',
    supervisor: 'Supervisor',
    lab_scientist: 'Laboratory Scientist',
    staff: 'Staff Member',
  };
  return roleMap[role] || 'Unknown';
};

/**
 * Converts department type to user-friendly display name
 *
 * @param department - Staff department type
 * @returns Human-readable department name
 */
export const getDepartmentDisplayName = (department: StaffDepartment): string => {
  const deptMap = {
    hematology: 'Hematology',
    biochemistry: 'Biochemistry',
    microbiology: 'Microbiology',
    pathology: 'Pathology',
    radiology: 'Radiology',
    administration: 'Administration',
    general: 'General',
  };
  return deptMap[department] || 'Unknown';
};

/**
 * Gets Tailwind color classes for a staff status
 * Used for visual status indicators
 *
 * @param status - Staff member status
 * @returns Tailwind text and background color classes
 */
export const getStatusColor = (status: StaffStatus): string => {
  const colorMap = {
    active: 'text-green-600 bg-green-100',
    inactive: 'text-gray-600 bg-gray-100',
    on_leave: 'text-blue-600 bg-blue-100',
    suspended: 'text-red-600 bg-red-100',
  };
  return colorMap[status] || 'text-gray-600 bg-gray-100';
};

/**
 * Gets Tailwind color classes for a role badge
 * Used for visual role indicators
 *
 * @param role - Staff role type
 * @returns Tailwind text and background color classes
 */
export const getRoleBadgeColor = (role: StaffRole): string => {
  const colorMap = {
    admin: 'text-teal-800 bg-teal-100',
    supervisor: 'text-purple-800 bg-purple-100',
    lab_scientist: 'text-blue-800 bg-blue-100',
    staff: 'text-gray-800 bg-gray-100',
  };
  return colorMap[role] || 'text-gray-800 bg-gray-100';
};